/**
 * Core's `421 WRONG_REGION`, read the same way wherever echo calls Core: the apps through
 * `client.ts`, and `apps/api` as the caller or as the Stronger Memory group. Plain functions with
 * no imports, so a server can use them without the React clients.
 */

/**
 * A `421 Misdirected Request` that Core sent because this region is not the person's home:
 * `code: WRONG_REGION` in a REST problem body, in a GraphQL error's `extensions`, or the
 * `OE-Region-Url` header that names the home.
 */
export function isWrongRegion(response: Response, data: unknown): boolean {
  if (response.headers.get('OE-Region-Url')) return true;
  const body = asRecord(data);
  if (body.code === 'WRONG_REGION') return true;
  return (Array.isArray(body.errors) ? body.errors : []).some(
    (error) => asRecord(asRecord(error).extensions).code === 'WRONG_REGION',
  );
}

/**
 * The home region's base URL from a 421: the `OE-Region-Url` header, else the body's
 * `base_url` (REST) or `extensions.baseUrl` (GraphQL; browsers only see the header if Core
 * exposes it). Undefined unless it is one of OnEarly's own API names (`REGION_DOMAINS`).
 */
export function homeRegionUrl(response: Response, data: unknown): string | undefined {
  const body = asRecord(data);
  const graphqlError = (Array.isArray(body.errors) ? body.errors : [])
    .map((error) => asRecord(asRecord(error).extensions))
    .find((extensions) => extensions.code === 'WRONG_REGION');
  const candidate = response.headers.get('OE-Region-Url') ?? body.base_url ?? graphqlError?.baseUrl;
  return typeof candidate === 'string' ? trustedRegionUrl(candidate) : undefined;
}

/**
 * Where a 421 may send the retry, which carries the bearer token: names under OnEarly's API
 * domains only (`api.us.onearly.com`, `api.us.staging.onearly.com`; `*.oe.test` for a local
 * Core), over https. A list rather than a rule derived from the configured name: "the last two
 * labels" of a name is no domain boundary (`co.uk`, or a trailing dot).
 */
const REGION_DOMAINS = ['onearly.com', 'oe.test'] as const;
const HTTPS_ORIGIN = /^https:\/\/((?:[a-z0-9-]+\.)+[a-z0-9-]+)\.?(?::\d{1,5})?\/?$/i;

export function trustedRegionUrl(candidate: string): string | undefined {
  const host = HTTPS_ORIGIN.exec(candidate.trim())?.[1]?.toLowerCase();
  if (!host || !REGION_DOMAINS.some((domain) => host.endsWith(`.${domain}`))) return undefined;
  return candidate.trim().replace(/\/$/, '');
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}
